import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import Modal from "../../Modal/Modal";
import TableAdv from "../../Table/TableAdv/TableAdv";
import SmartTooltip from "../../Info/SmartTooltip";
import PageTab from "../../DynamicTabs/PageTab";
import Backdrop from "../../Backgrounds/Backdrop";
import LogoLoader from "../../Loading/LogoLoader";
import { toTitleCase } from "../../../utils/common";

export default function StageModal({
  isOpen,
  onClose,
  data,
  employeeId,
  stageName,
  totalCount,
  page,
  limit,
  setPage,
  handleLimitChange,
  tab2Data,
  tab3Data,
  onStageNumberChange,
  tab2TotalCount,
  tab3TotalCount,
  loading
}) {
  const [activeTab, setActiveTab] = useState(0);
  useEffect(() => {
    onStageNumberChange?.(activeTab);
  }, [activeTab]);
  const employeeJobs = useMemo(() => {
    const jobs = Array.isArray(data) ? data : [];

    return jobs.filter(
      (job) => String(job?.addedBy?.uiid ?? "") === String(employeeId ?? "")
    );
  }, [data, employeeId]);

  // Candidate Rows
  const candidateRows = useMemo(() => {
    const rows = [];

    employeeJobs.forEach((job) => {
      job?.submissions?.forEach((sub) => {
        rows.push({
          candidateName: toTitleCase(sub?.candidateName) ?? "",

          submittedDate: sub?.statusHistory?.addedOn
            ? dayjs(sub.statusHistory.addedOn).format("MMM DD, YYYY")
            : "",

          jobTitle: toTitleCase(job?.jobTitle) ?? "",
          companyName: toTitleCase(job?.companyName) ?? "",
        });
      });
    });

    return rows;
  }, [employeeJobs]);

  // Jobs With Submissions
  const jobsWith = useMemo(() => {
    if (!Array.isArray(tab2Data)) return [];

    return (tab2Data || []).map((item) => ({
      jobTitle: toTitleCase(item?.jobTitle) ?? "-",
      companyName: toTitleCase(item?.companyName) ?? "-",
      totalSubmissions: item?.totalSubmissions ?? 0,
    }));
  }, [tab2Data]);

  // Jobs Without Submissions
  const jobsWithout = useMemo(() => {
    if (!Array.isArray(tab2Data)) return [];

    return (tab3Data || []).map((item) => ({
      jobTitle: toTitleCase(item?.jobTitle) ?? "-",
      companyName: toTitleCase(item?.companyName) ?? "-",
    }));
  }, [tab3Data]);

  const candidateColumns = useMemo(
    () => [
      { key: "candidateName", label: "Candidate Name" },
      {
        key: "submittedDate",
        label: "Submitted Date",
      },
      {
        key: "jobTitle",
        label: "Job Title",
        render: (row) => (
          <SmartTooltip description={row?.jobTitle} noicon>
            <div className="line-clamp-1">{row?.jobTitle}</div>
          </SmartTooltip>
        ),
      },
      {
        key: "companyName",
        label: "Company",
        width: "min-w-[220px]! max-w-[280px]!",
        render: (row) => (
          <SmartTooltip
            description={row?.companyName}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal={true}
          >
            <div className="line-clamp-1">{row?.companyName}</div>
          </SmartTooltip>
        ),
      },
    ],
    []
  );

  const jobWithColumns = useMemo(
    () => [
      {
        key: "jobTitle",
        label: "Job Title",
        width: "min-w-[260px]! max-w-[340px]!",
        render: (row) => (
          <SmartTooltip
            description={row?.jobTitle}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal={true}
          >
            <div className="line-clamp-1">{row?.jobTitle}</div>
          </SmartTooltip>
        ),
      },
      {
        key: "companyName",
        label: "Company",
        width: "min-w-[220px]! max-w-[280px]!",
        render: (row) => (
          <SmartTooltip
            description={row?.companyName}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal={true}
          >
            <div className="line-clamp-1">{row?.companyName}</div>
          </SmartTooltip>
        ),
      },
      {
        key: "totalSubmissions",
        label: "Total Submissions",
        width: "min-w-[180px]!",
      },
    ],
    []
  );

  const jobWithoutColumns = useMemo(
    () => [
      {
        key: "jobTitle",
        label: "Job Title",
        width: "min-w-[260px]! max-w-[340px]!",
        render: (row) => (
          <SmartTooltip
            description={row?.jobTitle}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal={true}
          >
            <div className="line-clamp-1">{row?.jobTitle}</div>
          </SmartTooltip>
        ),
      },
      {
        key: "companyName",
        label: "Company",
        width: "min-w-[220px]! max-w-[280px]!",
        render: (row) => (
          <SmartTooltip
            description={row?.companyName}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal={true}
          >
            <div className="line-clamp-1">{row?.companyName}</div>
          </SmartTooltip>
        ),
      },
    ],
    [],
  );

  const jobsAddedCount = useMemo(() => {
    if (!data || !totalCount) return 0;
    const stageMap = {
      "Internal Submission": "internalSubmissions",
      "Client Submissions": "clientSubmissions",
      "Aggrements" : "aggrements",
      "Interview Round 1": "interviewRound1",
      "Interview Round 2": "interviewRound2",
      "Interview Round 3": "interviewRound3",
      "Placements": "placed"
    };

    let total = 0;

    data.forEach(item => {
      const bdmId = item?.addedBy?.uiid;

      const match = totalCount.find(
        t => String(t?.addedBy?.uiid) === String(bdmId)
      );

      if (match) {
        total = Number(match?.submissions?.[stageMap[stageName]] || 0);
      }
    });

    return total;
  }, [data, totalCount]);

  const tabs = useMemo(
    () => [
      {
        title: `${stageName} Submissions (${jobsAddedCount})`,
        type: "main",
        page: (
          <div className="h-full overflow-hidden flex flex-col p-0.5">
            <TableAdv
              showSlNo
              columns={candidateColumns}
              rows={candidateRows}
              tableId="treeHierarchyStageCandidates"
              serverPagination={true}
              totalCount={jobsAddedCount}
              page={page}
              limit={limit}
              onPaginationChange={setPage}
              onLimitChange={handleLimitChange}
              toolbarRight={{
                search: true,
                filter: false,
                download: false,
                viewToggle: false,
                fullScreen: false,
                add: [],
              }}
              toolbarLeft={{ add: [] }}
            />
          </div>
        ),
      },
      {
        title: `Jobs with ${stageName} (${tab2TotalCount ?? 0})`,
        type: "main",
        page: (
          <div className="h-full overflow-hidden flex flex-col p-0.5">
            <TableAdv
              columns={jobWithColumns}
              rows={jobsWith}
              tableId="treeHierarchyStageJobsWith"
              serverPagination={true}
              totalCount={tab2TotalCount ?? 0}
              page={page}
              limit={limit}
              onPaginationChange={setPage}
              onLimitChange={handleLimitChange}
              toolbarRight={{
                search: true,
                filter: false,
                download: false,
                viewToggle: false,
                fullScreen: false,
                add: [],
              }}
              toolbarLeft={{ add: [] }}
            />
          </div>
        ),
      },
      {
        title: `Jobs without ${stageName} (${tab3TotalCount ?? 0})`,
        type: "main",
        page: (
          <div className="h-full overflow-hidden flex flex-col p-0.5">
            <TableAdv
              columns={jobWithoutColumns}
              rows={jobsWithout}
              serverPagination={true}
              totalCount={tab3TotalCount ?? 0}
              page={page}
              limit={limit}
              onPaginationChange={setPage}
              onLimitChange={handleLimitChange}
              tableId="treeHierarchyStageJobsWithout"
              toolbarRight={{
                search: true,
                filter: false,
                download: false,
                viewToggle: false,
                fullScreen: false,
                add: [],
              }}
              toolbarLeft={{ add: [] }}
            />
          </div>
        ),
      },
    ],
    [
      candidateColumns,
      candidateRows,
      jobWithColumns,
      jobWithoutColumns,
      jobsWith,
      jobsWithout,
      stageName,
      page,
      limit,
      setPage,
      handleLimitChange
    ]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        setActiveTab(0);
      }}
      title={stageName}
      width="max-w-6xl"
      bodyClassName="p-0 flex flex-1 overflow-hidden"
    >
      <div className="w-full max-h-[70vh] overflow-hidden p-2 flex flex-1 ">
        <PageTab
          tabs={tabs}
          defaultTab={0}
          variant="text"
          activeVariant="contained"
          color="black"
          activeColor="primary"
          onTabChange={(index) => setActiveTab(index)}
        />
      </div>
       <Backdrop isOpen={loading} zIndex={53}>
        <LogoLoader />
      </Backdrop>
    </Modal>
  );
}

